<!--
  The tutoring calendar: a month at a time, Compact (every week small, like a month view), Week
  (the default: the picked week shows each session in full, every other week is one summary line)
  or Detailed (every week in full), with a color per tutor.
  Tutors get the same calendar for their own sessions (Mine passes their data as the source). This is
  the one place to put sessions on the calendar: pick the student, and their usual tutor fills
  in (change it if someone else is covering). When a session's time has passed it logs itself:
  the hours come off the student's prepaid balance (or it's charged at their rate) and the
  tutor's pay is added up in Tutoring (credited in their overall total on People).
-->
<script lang="ts" module>
  import type { TutoringEntry, TutoringSlot } from "@split/core";
  export interface SlotPatch {
    startsAt: string;
    endsAt: string;
    studentId: string | null;
    tutorId: string | null;
    /** Waiting for a tutor (tutorId is null). */
    unclaimed: boolean;
    note: string;
  }
  /** Where the calendar's sessions come from, and what whoever is looking can do with them. */
  export interface CalendarSource {
    /**
     * "tutor": a tutor's own calendar: their lessons, their paired students' and any waiting for a
     * tutor (the server sends only those). Miles schedules; a tutor takes lessons and logs times.
     */
    mode: "owner" | "tutor";
    /** Tutor mode: their own tutor id. */
    me?: string;
    slots: TutoringSlot[];
    tutors: { id: string; name: string }[];
    students: { id: string; name: string; tutorId?: string | null }[];
    studentName: (id: string) => string;
    /** Put lessons on the calendar (Miles only). */
    add?: (b: { studentId: string; tutorId: string | null; unclaimed: boolean; date: string; start: string; end: string; weeks: number; note: string }) => Promise<boolean>;
    book?: (slot: TutoringSlot, studentId: string) => Promise<void>;
    cancel?: (slot: TutoringSlot) => Promise<void>;
    /** Change a lesson's date, times, student, tutor or note right on its card; absent: no Edit. */
    edit?: (slot: TutoringSlot, patch: SlotPatch) => Promise<void>;
    /** A tutor logs the exact start and end of their own lesson. */
    times?: (slot: TutoringSlot, start: string, end: string) => Promise<void>;
    /** A tutor takes a lesson waiting for a tutor (or asks for it, with no market price). */
    take?: (slot: TutoringSlot) => Promise<void>;
    /** A waiting lesson's market price for tutors (0: not listed). */
    marketPay?: (slot: TutoringSlot) => number;
    /** This tutor's own request for a waiting lesson, if one is waiting (its id). */
    asked?: (slot: TutoringSlot) => string | null;
    withdraw?: (claimId: string) => Promise<void>;
    /** early: "Mark done now" was confirmed for a lesson that hasn't ended. */
    done: (slot: TutoringSlot, done: boolean, early?: boolean) => Promise<void>;
    /** Logged sessions, to say how each done lesson was logged. */
    entries?: TutoringEntry[];
  }
</script>

<script lang="ts">
  import { fill, shows } from "../../settings/index.ts";
  import * as TT from "../../settings/tutoring.ts";
  import TimeInput from "../../components/TimeInput.svelte";
  import { hoursText, isUnclaimed, pairingPay, studentSummary } from "@split/core";
  import Requests from "./Requests.svelte";
  import { localNow, tutoring } from "../../lib/tutoring.svelte.ts";
  import { formatCents } from "../../lib/format.ts";
  import { app } from "../../lib/store.svelte.ts";

  let { source }: { source?: CalendarSource } = $props();

  /** Yours by default: everything in Tutoring. */
  const ownerSource: CalendarSource = {
    mode: "owner",
    get slots() {
      return tutoring.slots;
    },
    get tutors() {
      return tutoring.tutors.filter((t) => !t.archived);
    },
    get students() {
      return tutoring.students.filter((s) => !s.archived);
    },
    studentName: (id) => tutoring.students.find((s) => s.id === id)?.name ?? "Student",
    add: (b) => tutoring.addLessons(b),
    async book(s, studentId) {
      const st = tutoring.students.find((x) => x.id === studentId);
      await tutoring.update("slots", s.id, { studentId, status: "booked", ...(s.tutorId || !st?.tutorId ? {} : { tutorId: st.tutorId }) }, "Booked");
    },
    cancel: (s) => tutoring.update("slots", s.id, { status: "cancelled" }, "Cancelled"),
    edit: async (s, patch) => void (await tutoring.editLesson(s.id, patch, TT.CAL_EDIT_SAVED)),
    done: (s, d, early) => tutoring.markDone(s.id, d, early),
    get entries() {
      return tutoring.entries;
    },
  };
  const src = $derived(source ?? ownerSource);
  const owner = $derived(src.mode === "owner");

  const DAY = 86_400_000;
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  const plus = (date: string, n: number) => iso(new Date(Date.parse(`${date}T12:00:00Z`) + n * DAY));
  function mondayOf(date: string) {
    const d = new Date(`${date}T12:00:00Z`);
    d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7));
    return iso(d);
  }

  const params = typeof location !== "undefined" ? new URLSearchParams(location.search) : new URLSearchParams();
  // ---- views: Compact (every week small), Week (the picked week shows every session in full,
  // the rest small), Detailed (every week in full). One month at a time.
  // Week is where you start: opening the calendar or picking whose schedule shows this week.
  type View = "compact" | "week" | "detailed";
  let view = $state<View>("week");
  function setView(v: View) {
    view = v;
  }
  /**
   * Today in Los Angeles: lesson times are Los Angeles times, so the calendar opens on Los
   * Angeles's week whatever zone this device is in. Checked again whenever the app's day is.
   */
  const today = $derived.by(() => {
    void app.today;
    return localNow().slice(0, 10);
  });
  /** Any day in the month being shown. */
  let anchor = $state(localNow().slice(0, 10));
  /** The Monday of the week shown in full (Week view): this week to start with. */
  let picked = $state(mondayOf(localNow().slice(0, 10)));
  function pickWeek(day: string) {
    picked = mondayOf(day);
    if (view === "compact") setView("week");
  }
  /** A week's line: opens that week under it, or folds it again (Week view). Nothing disappears. */
  function toggleWeek(day: string) {
    if (view !== "week") return;
    picked = picked === mondayOf(day) ? "" : mondayOf(day);
  }

  const days = $derived.by(() => {
    const first = `${anchor.slice(0, 7)}-01`;
    const start = mondayOf(first);
    const nextMonth = new Date(`${first}T12:00:00Z`);
    nextMonth.setUTCMonth(nextMonth.getUTCMonth() + 1);
    const end = iso(nextMonth);
    const out: string[] = [];
    for (let d = start; d < end || out.length % 7; d = plus(d, 1)) out.push(d);
    return out;
  });
  const weeks = $derived(Array.from({ length: days.length / 7 }, (_, i) => days.slice(i * 7, i * 7 + 7)));
  function shift(n: number) {
    const d = new Date(`${anchor.slice(0, 7)}-01T12:00:00Z`);
    d.setUTCMonth(d.getUTCMonth() + n);
    anchor = iso(d);
    // Keep the full week inside the month you moved to.
    if (!days.includes(picked)) picked = mondayOf(anchor.slice(0, 7) === today.slice(0, 7) ? today : anchor);
  }
  function goToday() {
    anchor = today;
    picked = mondayOf(today);
  }
  const title = $derived(new Date(`${anchor.slice(0, 7)}-01T12:00:00Z`).toLocaleDateString("en-US", { month: "long", year: "numeric", timeZone: "UTC" }));
  const expanded = (week: string[]) => view === "detailed" || (view === "week" && week[0] === picked);
  /** "Oct 5–11", or "Sep 28–Oct 4" across months. */
  const span = (week: string[]) => {
    const a = new Date(`${week[0]}T12:00:00Z`);
    const b = new Date(`${week[6]}T12:00:00Z`);
    const m = (d: Date) => d.toLocaleDateString("en-US", { month: "short", timeZone: "UTC" });
    return `${m(a)} ${a.getUTCDate()}–${m(a) === m(b) ? "" : `${m(b)} `}${b.getUTCDate()}`;
  };
  /** One line for a week that isn't shown in full (Week view): how many lessons and where they stand. */
  function weekSummary(week: string[]) {
    const list = week.flatMap((d) => slotsOn(d));
    const lessons = list.filter((s) => s.studentId);
    const n = (k: (s: TutoringSlot) => boolean) => list.filter(k).length;
    return {
      lessons: lessons.length,
      done: n((s) => s.status === "done"),
      booked: n((s) => s.status === "booked"),
      missed: n((s) => missed(s)),
      open: n((s) => s.status === "open"),
      needs: n((s) => isUnclaimed(s)),
    };
  }
  /** Picking whose schedule (a category) goes to Week view on this week. */
  function pickWho(id: string) {
    who = id;
    setView("week");
    goToday();
  }

  // Whose schedule: everyone, just yours, or one tutor's (from "Their schedule" on Tutors).
  let who = $state<string>(params.get("tutor") ?? "all");
  /** A fixed color per tutor (you are always the first). */
  const colorOf = (id: string | null) => {
    const i = id ? tutoring.tutors.findIndex((t) => t.id === id) + 2 : 1;
    return `var(--series-${((Math.max(i, 2) - 1) % 8) + 1})`;
  };
  /** On a tutor's calendar: theirs in one color, a paired student's lesson with someone else in another. */
  const tutorColor = (id: string | null) => (owner ? colorOf(id) : id === src.me ? "var(--series-2)" : "var(--series-5)");
  const matches = (s: TutoringSlot, id: string) =>
    !owner || id === "all" || (id === "open" ? isUnclaimed(s) : id === "me" ? !s.tutorId && !isUnclaimed(s) : s.tutorId === id);
  /** Cancelled sessions drop off, except ones marked "Didn't happen" (so they can be switched back). */
  const missed = (s: TutoringSlot) => s.status === "cancelled" && s.outcome === "missed";
  const live = $derived(src.slots.filter((s) => s.status !== "cancelled" || missed(s)));
  const inView = $derived(live.filter((s) => s.startsAt >= `${days[0]}T00:00` && s.startsAt <= `${days[days.length - 1]}T99`));
  const countFor = (id: string) => inView.filter((s) => matches(s, id)).length;
  const slotsOn = (day: string) =>
    inView.filter((s) => s.startsAt.startsWith(day) && matches(s, who)).sort((a, b) => (a.startsAt < b.startsAt ? -1 : 1));
  const tutorName = (id: string | null) => (id ? (src.tutors.find((t) => t.id === id)?.name ?? "Tutor") : TT.CAL_YOU);
  /** Who teaches it, as a card says it: a tutor, you, or nobody yet. */
  const whoTeaches = (s: TutoringSlot) => (isUnclaimed(s) ? TT.CLAIM_NEEDS_TUTOR_TAG : tutorName(s.tutorId));
  const studentName = (id: string | null) => (id ? src.studentName(id) : "");
  /** 24-hour time, as stored: "16:00". */
  const time = (dt: string) => dt.slice(11, 16);
  const weekday = (d: string) => new Date(`${d}T12:00:00Z`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" });
  const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  /** Los Angeles time now, "2026-10-22T16:30" (lesson times are Los Angeles times). */
  const nowLocal = () => localNow();
  const started = (s: TutoringSlot) => s.startsAt <= nowLocal();
  const ended = (s: TutoringSlot) => s.endsAt <= nowLocal();

  // ---- done before it ends: say what it will do and ask first
  let confirming = $state<string | null>(null);
  const slotMinutes = (s: TutoringSlot) => Math.round((Date.parse(`${s.endsAt}:00Z`) - Date.parse(`${s.startsAt}:00Z`)) / 60_000);
  function earlyText(s: TutoringSlot): string {
    const mins = slotMinutes(s);
    const who = studentName(s.studentId);
    const parts: string[] = [];
    const hours = hoursText(mins);
    if (owner) {
      const st = tutoring.students.find((x) => x.id === s.studentId);
      parts.push(fill(st?.billing === "hourly" ? TT.CAL_EARLY_CHARGED : TT.CAL_EARLY_PREPAID, { student: who, hours }));
      const tu = s.tutorId ? tutoring.tutors.find((x) => x.id === s.tutorId) : null;
      if (tu) parts.push(fill(TT.CAL_EARLY_EARNS, { tutor: tu.name, amount: formatCents(Math.round((pairingPay(s.studentId, tu.id, tutoring.pairings) * mins) / 60)) }));
    } else parts.push(fill(TT.CAL_EARLY_TAUGHT, { hours }));
    return fill(TT.CAL_EARLY_TEXT, { time: time(s.endsAt), what: parts.join(TT.CAL_EARLY_JOIN) });
  }
  function markDone(s: TutoringSlot) {
    if (s.status === "done") return;
    if (!ended(s)) {
      confirming = s.id;
      return;
    }
    void src.done(s, true);
  }
  /** How a done lesson was logged: by itself when it ended, or marked done by hand. */
  function loggedText(s: TutoringSlot): string {
    const e = src.entries?.find((x) => x.kind === "session" && x.slotId === s.id);
    if (!e?.logged) return "";
    return e.logged === "auto" ? TT.CAL_LOGGED_AUTO : TT.CAL_LOGGED_HAND;
  }
  /** A lesson in a small (Compact) day: everything about it, for its title and screen readers. */
  const evText = (s: TutoringSlot) =>
    `${time(s.startsAt)}–${time(s.endsAt)} ${s.studentId ? studentName(s.studentId) : "Open"}${owner || isUnclaimed(s) || s.tutorId !== src.me ? ` · ${whoTeaches(s)}` : ""} · ${statusText(s)}`;
  const statusText = (s: TutoringSlot) =>
    s.status === "done" ? "Done" : missed(s) ? TT.CAL_MISSED : s.status === "open" ? "Open" : isUnclaimed(s) ? TT.CLAIM_NEEDS_TUTOR_TAG : "Booked";

  // ---- the one form for putting sessions on the calendar
  let adding = $state(false);
  let f = $state({ studentId: "", tutorId: "", date: localNow().slice(0, 10), start: "16:00", end: "17:00", repeat: false, weeks: "8", note: "" });
  /** The Tutor choice for "needs a tutor" (any tutor can take it). */
  const OPEN = "__open";
  /** A student's usual tutor, or "needs a tutor" when they're looking for one. */
  const usualTutor = (id: string) => {
    const st = tutoring.students.find((s) => s.id === id);
    return st?.tutorId ?? (st?.seeking ? OPEN : "");
  };
  function startAdding(date = today, studentId = "") {
    f = {
      studentId,
      tutorId: studentId ? usualTutor(studentId) : who !== "all" && who !== "me" ? (who === "open" ? OPEN : who) : "",
      date,
      start: "16:00",
      end: "17:00",
      repeat: false,
      weeks: "8",
      note: "",
    };
    adding = true;
  }
  // From "Schedule a session" on a student.
  const fromStudent = params.get("student");
  if (fromStudent) startAdding(localNow().slice(0, 10), fromStudent);

  function pickStudent(id: string) {
    f.studentId = id;
    if (owner) f.tutorId = usualTutor(id);
  }
  /** The lesson being scheduled has already ended (Los Angeles time): it logs as done on save. */
  const past = $derived(`${f.date}T${f.end}` <= localNow());
  const chosen = $derived(owner ? tutoring.students.find((s) => s.id === f.studentId) : undefined);
  const chosenSum = $derived(chosen ? studentSummary(chosen.id, tutoring.entries) : null);
  const minutesOf = (a: string, b: string) => {
    const [h1, m1] = a.split(":").map(Number) as [number, number];
    const [h2, m2] = b.split(":").map(Number) as [number, number];
    return h2 * 60 + m2 - (h1 * 60 + m1);
  };

  async function add(e: SubmitEvent) {
    e.preventDefault();
    if (!src.add) return;
    if (!f.studentId) return app.toast(TT.CAL_PICK_STUDENT, "error");
    if (f.end <= f.start) return app.toast("The end time is before the start", "error");
    const count = f.repeat ? Math.max(1, Math.min(26, Number(f.weeks) || 1)) : 1;
    const open = f.tutorId === OPEN;
    const ok = await src.add({ studentId: f.studentId, tutorId: open ? null : f.tutorId || null, unclaimed: open, date: f.date, start: f.start, end: f.end, weeks: count, note: f.note.trim() });
    if (!ok) return;
    app.toast(count > 1 ? `Added ${count} weeks` : "Added");
    adding = false;
    anchor = f.date;
    picked = mondayOf(f.date);
    if (view === "compact") setView("week");
  }

  // ---- changing a lesson right on its card: Miles changes anything, booked or done (a done
  // lesson's logged hours and pay change with it); a tutor logs the exact times of their own.
  let editing = $state<string | null>(null);
  /** "all": every field (Miles); "times": only the start and end (a tutor). */
  let editKind = $state<"all" | "times">("all");
  let ed = $state({ date: "", start: "", end: "", studentId: "", tutorId: "", note: "" });
  const editable = (s: TutoringSlot) => Boolean(src.edit) && (s.status !== "cancelled" || missed(s));
  /** A tutor's own lesson that happened or is booked: they can log its exact times. */
  const timeable = (s: TutoringSlot) => Boolean(src.times) && !!s.tutorId && s.tutorId === src.me && (s.status !== "cancelled" || missed(s));
  function startEdit(s: TutoringSlot, kind: "all" | "times" = "all") {
    confirming = null;
    booking = null;
    editKind = kind;
    ed = {
      date: s.startsAt.slice(0, 10),
      start: time(s.startsAt),
      end: time(s.endsAt),
      studentId: s.studentId ?? "",
      tutorId: isUnclaimed(s) ? OPEN : (s.tutorId ?? ""),
      note: s.note ?? "",
    };
    editing = s.id;
  }
  async function saveEdit(e: SubmitEvent, s: TutoringSlot) {
    e.preventDefault();
    if (ed.end <= ed.start) return app.toast(TT.CAL_EDIT_BAD_TIME, "error");
    if (editKind === "times") {
      await src.times?.(s, ed.start, ed.end);
      editing = null;
      return;
    }
    const open = ed.tutorId === OPEN;
    await src.edit?.(s, {
      startsAt: `${ed.date}T${ed.start}`,
      endsAt: `${ed.date}T${ed.end}`,
      studentId: ed.studentId || null,
      tutorId: open ? null : ed.tutorId || null,
      unclaimed: open,
      note: ed.note.trim(),
    });
    editing = null;
  }
  /** On a tutor's calendar: a lesson waiting for a tutor shows its pay (when listed) and Take. */
  const payText = (s: TutoringSlot) => {
    const cents = src.marketPay?.(s) ?? 0;
    return cents ? fill(TT.CLAIM_PAY, { pay: `${formatCents(cents)}/hr` }) : TT.CLAIM_PAY_UNLISTED;
  };

  let booking = $state<string | null>(null);
  let bookStudent = $state("");
  async function book(s: TutoringSlot) {
    if (!bookStudent) return app.toast("Pick a student", "error");
    await src.book?.(s, bookStudent);
    booking = null;
  }
</script>

<div class="head">
  <div class="nav">
    <button class="btn small" onclick={() => shift(-1)} aria-label="Previous month">‹</button>
    <strong class="title">{title}</strong>
    <button class="btn small" onclick={() => shift(1)} aria-label="Next month">›</button>
    <button class="btn small quiet" onclick={goToday}>Today</button>
  </div>
  <div class="nav">
    <span class="seg" role="group" aria-label="Calendar view">
      <button aria-pressed={view === "compact"} onclick={() => setView("compact")}>Compact</button>
      <button aria-pressed={view === "week"} onclick={() => setView("week")}>Week</button>
      <button aria-pressed={view === "detailed"} onclick={() => setView("detailed")}>Detailed</button>
    </span>
    {#if !adding && src.add}<button class="btn primary" onclick={() => startAdding()}>Schedule a session</button>{/if}
  </div>
</div>

{#if owner}
  <Requests />
  <div class="who" role="group" aria-label="Whose schedule">
    {#each [["all", "Everyone"], ["me", TT.CAL_YOU], ["open", TT.CAL_CHIP_OPEN], ...src.tutors.map((t) => [t.id, t.name])] as [id, label] (id)}
      <button class="chip" aria-pressed={who === id} onclick={() => pickWho(id!)}>
        {#if id !== "all" && id !== "open"}<span class="dot" style:background={colorOf(id === "me" ? null : id!)}></span>{/if}
        {label}
        <span class="n">{countFor(id!)}</span>
      </button>
    {/each}
  </div>
{/if}

{#if adding}
  <form class="panel" onsubmit={add}>
    <h2>Schedule a session</h2>
    <div class="grid">
      <label class="field"
        ><span>Student</span>
        <select class="input" value={f.studentId} onchange={(e) => pickStudent((e.currentTarget as HTMLSelectElement).value)} required>
          <option value="" disabled>{TT.CAL_PICK_STUDENT}</option>
          {#each src.students as s (s.id)}<option value={s.id}>{s.name}</option>{/each}
        </select></label
      >
      {#if owner}
        <label class="field"
          ><span>Tutor</span>
          <select class="input" bind:value={f.tutorId}>
            <option value="">{TT.CAL_YOU}</option>
            <option value={OPEN}>{TT.CLAIM_NEEDS_TUTOR}</option>
            {#each src.tutors as t (t.id)}<option value={t.id}>{t.name}</option>{/each}
          </select></label
        >
      {/if}
      <label class="field"><span>Date</span><input class="input" type="date" bind:value={f.date} /></label>
      <div class="field"><span>From</span><TimeInput bind:value={f.start} label="From" /></div>
      <div class="field"><span>To</span><TimeInput bind:value={f.end} label="To" /></div>
    </div>
    <label class="check"><input type="checkbox" bind:checked={f.repeat} /> Repeat weekly for <input class="input weeks" bind:value={f.weeks} inputmode="numeric" aria-label="Weeks" /> weeks</label>
    <label class="field"><span>Note</span><input class="input" bind:value={f.note} placeholder="Subject, place, link" /></label>
    {#if past && f.tutorId !== OPEN && shows(TT.CAL_PAST_NOTE)}<p class="muted small" role="note">{TT.CAL_PAST_NOTE}</p>{/if}
    {#if TT.SHOW_CAL_FORM_NOTE && chosen && chosenSum}
      {@const mins = Math.max(0, minutesOf(f.start, f.end))}
      <p class="muted small">
        {#if chosenSum.boughtMinutes}
          {hoursText(mins)} comes off {chosen.name}'s prepaid hours ({hoursText(chosenSum.leftMinutes)} left now).
        {:else if chosen.billing === "hourly"}
          {chosen.name} is charged for {hoursText(mins)} at their rate.
        {:else}
          {chosen.name} has no prepaid hours yet; add them on Students.
        {/if}
        {#if f.tutorId && f.tutorId !== OPEN}{tutorName(f.tutorId)} is paid for it.{/if}
        Sessions log themselves once they're over{f.date < today ? " (this one right away)" : ""}.
      </p>
    {/if}
    <div class="btns">
      <button class="btn primary">Save</button>
      <button type="button" class="btn quiet" onclick={() => (adding = false)}>Cancel</button>
    </div>
  </form>
{/if}

{#snippet card(s: TutoringSlot)}
  <div class="slot {s.status}" class:missed={missed(s)} class:unclaimed={isUnclaimed(s)} style:border-left-color={s.status === "open" || isUnclaimed(s) ? undefined : tutorColor(s.tutorId)}>
    {#if editing === s.id}
      <form class="edit" onsubmit={(e) => saveEdit(e, s)}>
        {#if editKind === "all"}<label class="field"><span>{TT.CAL_EDIT_DATE}</span><input class="input" type="date" bind:value={ed.date} /></label>{/if}
        <span class="times">
          <span class="field"><span>{TT.CAL_EDIT_FROM}</span><TimeInput bind:value={ed.start} label={TT.CAL_EDIT_FROM} /></span>
          <span class="field"><span>{TT.CAL_EDIT_TO}</span><TimeInput bind:value={ed.end} label={TT.CAL_EDIT_TO} /></span>
        </span>
        {#if editKind === "all"}
          <label class="field"><span>{TT.CAL_EDIT_STUDENT}</span>
            <select class="input" bind:value={ed.studentId}>
              {#if s.status !== "done"}<option value="">{TT.CAL_EDIT_OPEN_TIME}</option>{/if}
              {#each src.students as x (x.id)}<option value={x.id}>{x.name}</option>{/each}
            </select></label>
          {#if owner}
            <label class="field"><span>{TT.CAL_EDIT_TUTOR}</span>
              <select class="input" bind:value={ed.tutorId}>
                <option value="">{TT.CAL_EDIT_YOU}</option>
                {#if s.status !== "done"}<option value={OPEN}>{TT.CLAIM_NEEDS_TUTOR}</option>{/if}
                {#each src.tutors as t (t.id)}<option value={t.id}>{t.name}</option>{/each}
              </select></label>
          {/if}
          <label class="field"><span>{TT.CAL_EDIT_NOTE}</span><input class="input" bind:value={ed.note} /></label>
        {/if}
        <span class="btns-row">
          <button class="btn small primary">{TT.CAL_EDIT_SAVE}</button>
          <button type="button" class="btn small quiet" onclick={() => (editing = null)}>{TT.CAL_EDIT_CANCEL}</button>
        </span>
      </form>
    {:else}
    <span class="t">{time(s.startsAt)}–{time(s.endsAt)}</span>
    <span class="w">{s.studentId ? studentName(s.studentId) : "Open"}</span>
    {#if owner}<span class="muted small" class:needs={isUnclaimed(s)}>{whoTeaches(s)}</span>
    {:else if isUnclaimed(s)}<span class="small needs">{TT.CLAIM_NEEDS_TUTOR_TAG}</span>{#if shows(payText(s))}<span class="small muted">{payText(s)}</span>{/if}
    {:else if s.tutorId !== src.me && shows(TT.CAL_WITH)}<span class="muted small">{fill(TT.CAL_WITH, { name: whoTeaches(s) })}</span>{/if}
    {#if s.note}<span class="muted small">{s.note}</span>{/if}
    {#if s.timesBy && shows(TT.CAL_TIMES_BY)}<span class="muted small">{fill(TT.CAL_TIMES_BY, { name: s.timesBy })}</span>{/if}
    {#if !owner && isUnclaimed(s)}
      {@const mine = src.asked?.(s) ?? null}
      {#if mine}
        <span class="small muted">{TT.CLAIM_WAITING}</span>
        {#if src.withdraw}<span class="ops"><button class="link-btn" onclick={() => src.withdraw?.(mine)}>{TT.CLAIM_WITHDRAW}</button></span>{/if}
      {:else if src.take}
        <span class="ops"><button class="btn small primary" onclick={() => src.take?.(s)}>{(src.marketPay?.(s) ?? 0) ? TT.CLAIM_TAKE_LESSON : TT.CLAIM_ASK_LESSON}</button></span>
      {/if}
    {:else if s.studentId && (s.status === "done" || missed(s) || s.status === "booked") && !isUnclaimed(s) && (owner || s.tutorId === src.me)}
      <span class="seg small-seg" role="group" aria-label="Did {studentName(s.studentId)}'s {time(s.startsAt)} session happen">
        <button aria-pressed={s.status === "done"} onclick={() => markDone(s)}>Done</button>
        <button aria-pressed={missed(s)} onclick={() => { confirming = null; if (!missed(s)) void src.done(s, false); }}>Not done</button>
      </span>
      {#if confirming === s.id && s.status !== "done"}
        <span class="confirm" role="alertdialog" aria-label="Mark {studentName(s.studentId)}'s {time(s.startsAt)} lesson done before it ends">
          <span class="small">{earlyText(s)}</span>
          <span class="btns-row">
            <button class="btn small primary" onclick={() => { confirming = null; void src.done(s, true, true); }}>Mark done now</button>
            <button class="btn small quiet" onclick={() => (confirming = null)}>Not yet</button>
          </span>
        </span>
      {:else if missed(s)}{#if shows(TT.CAL_MISSED)}<span class="small muted">{TT.CAL_MISSED}</span>{/if}
      {:else if s.status === "booked"}{#if shows(started(s) ? TT.CAL_ON_NOW_NOTE : TT.CAL_BOOKED_NOTE)}<span class="small muted">{fill(started(s) ? TT.CAL_ON_NOW_NOTE : TT.CAL_BOOKED_NOTE, { time: time(s.endsAt) })}</span>{/if}
      {:else if s.status === "done" && shows(loggedText(s))}<span class="small muted">{loggedText(s)}</span>{/if}
    {:else}
      <span class="small muted st">{statusText(s)}</span>
    {/if}
    {#if owner || s.status === "open" || timeable(s)}
    <span class="ops">
      {#if s.status === "open" && src.book}
        {#if booking === s.id}
          <select class="input mini" bind:value={bookStudent} aria-label="Student">
            <option value="">Student…</option>
            {#each src.students as x (x.id)}<option value={x.id}>{x.name}</option>{/each}
          </select>
          <button class="link-btn" onclick={() => book(s)}>Book</button>
        {:else}
          <button class="link-btn" onclick={() => { booking = s.id; bookStudent = ""; }}>Book</button>
        {/if}
      {/if}
      {#if editable(s)}<button class="link-btn" onclick={() => startEdit(s)}>{TT.CAL_EDIT}</button>{/if}
      {#if timeable(s)}<button class="link-btn" onclick={() => startEdit(s, "times")}>{TT.CAL_TIMES}</button>{/if}
      {#if src.cancel && (s.status === "open" || (s.status === "booked" && !started(s)))}<button class="link-btn muted" onclick={() => src.cancel?.(s)}>Cancel</button>{/if}
    </span>
    {/if}
    {/if}
  </div>
{/snippet}

<!-- One layout per width, same contents: wide, a week is 7 day columns; narrow (phones and
     tablets), a week shown in full becomes a list of days with full-width lesson cards. -->
<div class="cal">
<div class="month" class:compact={view === "compact"} role="grid" aria-label={title}>
  {#each WEEKDAYS as w (w)}<div class="wd" role="columnheader">{w}</div>{/each}
  {#each weeks as week (week[0])}
    {@const full = expanded(week)}
    {#if view !== "compact"}
      {@const w = weekSummary(week)}
      <button class="wsum" class:open={full} disabled={view !== "week"} onclick={() => toggleWeek(week[0]!)} aria-expanded={full} aria-label="{span(week)}: {w.lessons} {w.lessons === 1 ? 'lesson' : 'lessons'}">
        <span class="wr">{span(week)}</span>
        {#if w.lessons || w.open}
          <span class="wn">{w.lessons} {w.lessons === 1 ? "lesson" : "lessons"}</span>
          {#if w.done}<span class="wt"><i class="sw done"></i>{w.done} done</span>{/if}
          {#if w.booked}<span class="wt"><i class="sw pend"></i>{w.booked} booked</span>{/if}
          {#if w.missed}<span class="wt muted">{w.missed} didn't happen</span>{/if}
          {#if w.open}<span class="wt muted">{w.open} open {w.open === 1 ? "time" : "times"}</span>{/if}
          {#if w.needs && shows(TT.OV_TUTORING_NEEDS)}<span class="wt needs">{fill(TT.OV_TUTORING_NEEDS, { n: w.needs })}</span>{/if}
        {:else}
          {#if shows(TT.CAL_WEEK_NONE)}<span class="wt muted">{TT.CAL_WEEK_NONE}</span>{/if}
        {/if}
        {#if view === "week"}<span class="wgo" aria-hidden="true"><span class="chev">›</span>{#if shows(full ? TT.CAL_WEEK_CLOSE : TT.CAL_WEEK_OPEN)}<span class="wgo-t">{full ? TT.CAL_WEEK_CLOSE : TT.CAL_WEEK_OPEN}</span>{/if}</span>{/if}
      </button>
    {/if}
    {#if full || view === "compact"}
    {#each week as d (d)}
      {@const list = slotsOn(d)}
      <div class="cell" class:full class:empty={!list.length} class:picked={view === "week" && week[0] === picked} class:out={d.slice(0, 7) !== anchor.slice(0, 7)} class:today={d === today} role="gridcell" aria-label={weekday(d)}>
        <button class="num" onclick={() => pickWeek(d)} aria-label="Show the week of {weekday(d)} in full"><span class="dn">{Number(d.slice(8))}</span>{#if full}<span class="dl">{weekday(d)}</span>{/if}</button>
        {#if full}
          {#each list as s (s.id)}{@render card(s)}{/each}
          {#if !list.length}<button class="add-cell" onclick={() => startAdding(d)} aria-label="Schedule on {weekday(d)}">+</button>{/if}
        {:else}
          {#each list.slice(0, 3) as s (s.id)}
            <button class="ev {s.status}" class:missed={missed(s)} class:open={isUnclaimed(s)} style:--c={s.status === "open" ? "var(--rule-strong)" : isUnclaimed(s) ? "var(--focus)" : tutorColor(s.tutorId)} onclick={() => pickWeek(d)} title={evText(s)} aria-label={evText(s)}>
              <span class="et">{time(s.startsAt)}</span>
              <span class="en">{s.studentId ? studentName(s.studentId) : "Open"}</span>
            </button>
          {/each}
          {#if list.length > 3}<button class="more" onclick={() => pickWeek(d)} aria-label="{list.length - 3} more on {weekday(d)}">+{list.length - 3}<span class="mw">&nbsp;more</span></button>{/if}
          {#if !list.length}<button class="add-cell" onclick={() => startAdding(d)} aria-label="Schedule on {weekday(d)}">+</button>{/if}
        {/if}
      </div>
    {/each}
    {/if}
  {/each}
</div>
</div>

<style>
  .head {
    display: flex;
    justify-content: space-between;
    gap: 10px;
    align-items: center;
    flex-wrap: wrap;
    margin-bottom: 12px;
  }

  .nav {
    display: flex;
    gap: 8px;
    align-items: center;
    flex-wrap: wrap;
  }

  .title {
    min-width: 130px;
    text-align: center;
  }

  .seg {
    display: inline-flex;
    gap: 2px;
    padding: 3px;
    border-radius: 999px;
    background: var(--wash);
  }

  .seg button {
    min-height: 30px;
    padding: 3px 14px;
    border: 0;
    border-radius: 999px;
    background: none;
    color: var(--ink-2);
    font: inherit;
    font-size: var(--t-sm);
    font-weight: 600;
    cursor: pointer;
  }

  .seg button[aria-pressed="true"] {
    background: var(--surface);
    color: var(--ink);
    box-shadow: 0 1px 2px rgb(0 0 0 / 0.12);
  }

  .who {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
    margin-bottom: 12px;
  }

  .chip {
    display: inline-flex;
    gap: 6px;
    align-items: center;
    min-height: 32px;
    padding: 4px 12px;
    border: 0;
    border-radius: 999px;
    background: var(--surface);
    box-shadow: inset 0 0 0 1px var(--rule);
    color: var(--ink-2);
    font: inherit;
    font-size: var(--t-sm);
    font-weight: 600;
    cursor: pointer;
  }

  .chip[aria-pressed="true"] {
    background: var(--ink);
    color: var(--surface);
    box-shadow: none;
  }

  .chip .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
  }

  .chip .n {
    opacity: 0.7;
    font-variant-numeric: tabular-nums;
  }

  .panel {
    display: grid;
    gap: 10px;
    padding: 16px;
    margin-bottom: 14px;
    border-radius: var(--r-lg);
    background: var(--surface);
  }

  .panel h2 {
    margin: 0;
  }

  .grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 10px;
  }

  .weeks {
    display: inline-block;
    width: 56px;
    min-height: 28px;
    padding: 2px 6px;
  }

  .btns {
    display: flex;
    gap: 8px;
  }

  .slot {
    display: grid;
    gap: 2px;
    padding: 8px;
    border-radius: 8px;
    border-left: 4px solid var(--series-1);
    background: var(--wash);
    font-size: var(--t-sm);
  }

  .slot.open {
    border-left-color: var(--rule-strong);
    border-left-style: dashed;
  }

  /* Waiting for a tutor: a dashed edge in the focus color, and its tag. */
  .slot.unclaimed {
    border-left-color: var(--focus);
    border-left-style: dashed;
  }

  .needs {
    color: var(--focus);
    font-weight: 650;
  }

  .slot.done {
    opacity: 0.9;
  }

  .slot.missed,
  .ev.missed {
    opacity: 0.6;
  }

  .slot.missed .t,
  .slot.missed .w,
  .ev.missed .en {
    text-decoration: line-through;
  }

  .slot {
    min-width: 0;
    overflow-wrap: anywhere;
  }

  .confirm {
    display: grid;
    gap: 6px;
    padding: 8px;
    border-radius: var(--r-md, 8px);
    background: var(--wash);
    white-space: normal;
  }

  .btns-row {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }

  .small-seg {
    display: flex;
    flex-wrap: wrap;
    padding: 2px;
    border-radius: 10px;
    /* A shade darker than the card it sits on, so both answers read as buttons. */
    background: color-mix(in srgb, var(--ink) 7%, transparent);
  }

  /* In a narrow day column the two answers stack rather than get cut off. */
  .small-seg button {
    flex: 1 1 auto;
    min-height: 26px;
    padding: 2px 6px;
    border-radius: 8px;
    font-size: var(--t-xs);
    white-space: nowrap;
  }

  .btns-row .btn {
    min-width: 0;
    white-space: normal;
  }

  .small-seg button[aria-pressed="true"]:first-child {
    color: var(--ok, #2f7d4f);
  }

  /* The week shown in full: its cells grow to fit every session. */
  .cell.full {
    gap: 6px;
  }

  .cell.picked {
    background: color-mix(in srgb, var(--focus) 5%, var(--surface));
  }

  .t {
    font-weight: 650;
  }

  .ops {
    display: flex;
    gap: 8px;
    align-items: center;
    flex-wrap: wrap;
  }

  .mini {
    min-height: 28px;
    padding: 2px 6px;
    font-size: var(--t-xs);
  }

  .add-cell {
    border: 1px dashed var(--rule);
    border-radius: 8px;
    background: none;
    color: var(--muted);
    font: inherit;
    cursor: pointer;
    opacity: 0;
    transition: opacity 0.15s;
  }

  .cell:hover .add-cell,
  .add-cell:focus-visible {
    opacity: 1;
  }

  @media (hover: none) {
    .add-cell {
      opacity: 0.6;
    }
  }

  /* ---- a week shown as one line (Week view) */
  .wsum {
    grid-column: 1 / -1;
    display: flex;
    gap: 14px;
    align-items: center;
    min-height: 44px;
    padding: 8px 12px;
    border: 0;
    background: var(--surface);
    color: var(--ink);
    font: inherit;
    font-size: var(--t-sm);
    text-align: left;
    cursor: pointer;
  }

  .wsum:hover {
    background: color-mix(in srgb, var(--focus) 4%, var(--surface));
  }

  .wr {
    min-width: 120px;
    font-weight: 650;
    font-variant-numeric: tabular-nums;
  }

  .wn {
    font-weight: 600;
  }

  .wt {
    display: inline-flex;
    gap: 5px;
    align-items: center;
    color: var(--ink-2);
  }

  .sw {
    width: 8px;
    height: 8px;
    border-radius: 2px;
  }

  .sw.done {
    background: var(--ok);
  }

  .sw.pend {
    background: var(--pending);
  }

  .wsum:disabled {
    cursor: default;
  }

  .wsum.open {
    background: var(--wash);
  }

  .wgo {
    display: inline-flex;
    gap: 6px;
    align-items: center;
    margin-left: auto;
    color: var(--focus);
    font-size: var(--t-xs);
    font-weight: 600;
  }

  .chev {
    display: inline-block;
    font-size: var(--t-md);
    line-height: 1;
    transition: transform 200ms cubic-bezier(0.2, 0.8, 0.2, 1);
  }

  .wsum.open .chev {
    transform: rotate(90deg);
  }

  @media (prefers-reduced-motion: reduce) {
    .chev {
      transition: none;
    }
  }

  .edit {
    display: grid;
    gap: 6px;
  }

  .edit .times {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 6px;
  }

  .edit .field > span:first-child {
    font-size: var(--t-xs);
    color: var(--muted);
  }

  @media (max-width: 640px) {
    .wsum {
      flex-wrap: wrap;
      gap: 4px 10px;
    }

    .wr {
      min-width: 0;
    }

    .wgo-t {
      display: none;
    }
  }

  /* ---- month */
  .month {
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
    gap: 1px;
    overflow: hidden;
    border-radius: var(--r-lg);
    background: var(--rule);
    box-shadow: 0 0 0 1px var(--rule);
  }

  .wd {
    padding: 6px 8px;
    background: var(--wash);
    color: var(--muted);
    font-size: var(--t-xs);
    font-weight: 650;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .cell {
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-height: 104px;
    min-width: 0;
    overflow: hidden;
    padding: 6px;
    background: var(--surface);
  }

  .cell.out {
    background: color-mix(in srgb, var(--surface) 70%, var(--wash));
  }

  .cell.out .num {
    color: var(--muted);
  }

  .num {
    align-self: flex-start;
    min-width: 26px;
    height: 26px;
    padding: 0 6px;
    border: 0;
    border-radius: 999px;
    background: none;
    color: var(--ink);
    font: inherit;
    font-size: var(--t-sm);
    font-weight: 650;
    cursor: pointer;
  }

  .cell.today .num {
    background: var(--focus);
    color: #fff;
  }

  .ev {
    display: flex;
    gap: 4px;
    align-items: baseline;
    min-width: 0;
    padding: 2px 6px;
    border: 0;
    border-left: 3px solid var(--c);
    border-radius: 4px;
    background: color-mix(in srgb, var(--c) 14%, var(--surface));
    color: var(--ink);
    font: inherit;
    font-size: var(--t-xs);
    text-align: left;
    cursor: pointer;
  }

  .ev.open {
    border-left-style: dashed;
    background: var(--wash);
  }

  .ev.done {
    opacity: 0.75;
  }

  .et {
    flex: none;
    color: var(--ink-2);
    font-variant-numeric: tabular-nums;
  }

  .en {
    overflow: hidden;
    font-weight: 600;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .more {
    align-self: flex-start;
    padding: 0 6px;
    border: 0;
    background: none;
    color: var(--muted);
    font: inherit;
    font-size: var(--t-xs);
    cursor: pointer;
  }

  .add-cell {
    flex: 1;
    min-height: 24px;
  }

  .dl {
    display: none;
  }

  /* ---- narrow (phones, tablets): too little room for 7 columns of lesson cards, so a week
     shown in full is a list of days, each lesson card the full width. Compact stays a small grid. */
  .cal {
    container-type: inline-size;
  }

  @container (max-width: 759px) {
    .month:not(.compact) .wd {
      display: none;
    }

    .cell.full {
      grid-column: 1 / -1;
      gap: 8px;
      min-height: 0;
      padding: 10px 12px;
    }

    .cell.full.empty {
      flex-direction: row;
      align-items: center;
    }

    .cell.full .num {
      align-self: center;
      min-height: 36px;
      padding: 0 10px;
    }

    .cell.full:not(.empty) .num {
      align-self: flex-start;
    }

    .cell.full .dn {
      display: none;
    }

    .cell.full .dl {
      display: inline;
    }

    .cell.full .add-cell {
      flex: none;
      width: 44px;
      min-height: 36px;
      margin-left: auto;
      opacity: 1;
    }

    .slot {
      padding: 10px 12px;
    }

    .small-seg button {
      min-height: 40px;
      font-size: var(--t-sm);
    }
  }

  /* Phone Compact: a day column is too narrow for names, so each lesson shows its start time in
     its tutor's color (the name is in its title; tapping opens that week with everything). */
  @container (max-width: 480px) {
    .cell {
      min-height: 64px;
      padding: 3px;
    }

    .en {
      display: none;
    }

    .ev {
      justify-content: center;
      padding: 1px 2px;
      border-left-width: 2px;
    }

    .more .mw {
      display: none;
    }
  }

  .small {
    font-size: var(--t-xs);
  }
</style>
